import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, errMessage } from "@/portal/api";
import { useAuth } from "@/portal/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import ServiceIcon from "@/components/ServiceIcon";
import { SERVICES, FAQS } from "@/data/content";
import { cn } from "@/lib/utils";
import {
  Loader2, Plus, X, AlertCircle, CheckCircle2, FileText, Mail, FileSignature, ScrollText,
  ArrowRight, DollarSign, Scale, Handshake, ShieldCheck, LineChart, Layers,
} from "lucide-react";
import { toast } from "sonner";

const SERVICE_LABELS = { electricity: "Commercial Electricity", gas: "Commercial Natural Gas", battery: "Virtual Power Plant Network" };

const STEPS = [
  { icon: FileText, title: "Submit your bill", desc: "Share your details and upload a recent electric and/or gas bill. It takes just a few minutes." },
  { icon: Mail, title: "Receive an LOA", desc: "You'll get a Letter of Authorization by email so we can review your billing and usage history." },
  { icon: ScrollText, title: "Review a proposal", desc: "We prepare a clear proposal outlining your projected savings and recommended contract terms." },
  { icon: FileSignature, title: "Execute a contract", desc: "Lock in your new rate, timed to your existing contract's expiration so there's no overlap." },
];

const WHY = [
  { icon: DollarSign, title: "No cost to you", desc: "Our brokerage service is free to your business. Suppliers compensate us, and we disclose exactly how so there are no surprises." },
  { icon: Scale, title: "Independent & unbiased", desc: "We aren't owned by any supplier. We represent your business and negotiate in your best interest." },
  { icon: Handshake, title: "Providers compete for you", desc: "We run a competitive process so vetted suppliers bid against each other to win your contract." },
  { icon: ShieldCheck, title: "Transparent terms", desc: "Clear contracts, no hidden fees, and a plain-English explanation of every option before you sign." },
  { icon: LineChart, title: "Ongoing management", desc: "We monitor markets and renewal windows so your rate never lapses to expensive default pricing." },
  { icon: Layers, title: "Built for multi-site", desc: "Consolidate electricity, natural gas, and Virtual Power Plant hosting across every location into one strategy." },
];

const NAV = [
  { id: "about", label: "Program" },
  { id: "services", label: "Services" },
  { id: "how", label: "How it works" },
  { id: "why", label: "Why IEG" },
  { id: "faq", label: "FAQ" },
];

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function PublicApply() {
  const { refCode } = useParams();
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [rep, setRep] = useState(null);
  const [notActive, setNotActive] = useState(false);
  const [elig, setElig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "",
    company_name: "", dm_name: "", dm_title: "", dm_email: "", dm_phone: "",
    state: "", agreement_expiration: "", notes: "",
  });
  const [services, setServices] = useState([]);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (refCode) {
      api.get(`/api/portal/public/rep/${refCode}`).then((r) => setRep(r.data.rep)).catch(() => setNotActive(true));
    }
    api.get("/api/portal/eligibility").then((r) => setElig(r.data)).catch(() => {});
  }, [refCode]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const stateInfo = elig && form.state ? elig.eligibility[form.state] : null;
  const available = stateInfo?.available || [];
  const toggleService = (s) => setServices((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));
  const addFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) setFiles((p) => [...p, { file: f, label: "electric_bill" }]);
  };
  const setFileLabel = (i, label) => setFiles((p) => p.map((x, idx) => (idx === i ? { ...x, label } : x)));
  const removeFile = (i) => setFiles((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.state) return toast.error("Please select a state.");
    if (services.length === 0) return toast.error("Please select at least one service.");
    setBusy(true);
    try {
      const payload = {
        account: { email: form.email, password: form.password, name: form.name, phone: form.phone, company_name: form.company_name },
        application: {
          company_name: form.company_name,
          decision_maker: { name: form.dm_name || form.name, title: form.dm_title, email: form.dm_email || form.email, phone: form.dm_phone || form.phone },
          state: form.state,
          services,
          agreement_expiration: form.agreement_expiration || null,
          notes: form.notes,
        },
      };
      const url = refCode ? `/api/portal/public/apply/${refCode}` : "/api/portal/public/apply";
      const { data } = await api.post(url, payload);
      setSession(data.token, data.user);
      const appId = data.application.id;
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f.file);
        fd.append("label", f.label);
        try { await api.post(`/api/portal/applications/${appId}/files`, fd); } catch { /* continue */ }
      }
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const states = elig ? Object.entries(elig.states).sort((a, b) => a[1].localeCompare(b[1])) : [];

  if (notActive) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ice px-6 text-center">
        <div>
          <AlertCircle className="mx-auto h-10 w-10 text-slatey" />
          <h1 className="mt-4 font-heading text-2xl font-semibold text-navy">This link isn't active</h1>
          <p className="mt-2 text-slatey">Please check the link or contact your Innervation Energy Group representative.</p>
          <Link to="/" className="mt-4 inline-block text-sm font-medium text-electric hover:underline">Visit innervation.energy →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky self-contained header */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-navy/95 backdrop-blur">
        <div className="mx-auto flex h-[68px] max-w-shell items-center justify-between px-5 md:px-10">
          <img src="/ieg-lockup-dark.png?v=4" alt="Innervation Energy Group" className="h-8 w-auto md:h-9" />
          <nav className="hidden items-center gap-7 lg:flex">
            {NAV.map((n) => (
              <button key={n.id} onClick={() => scrollToId(n.id)} className="text-sm font-medium text-white/70 transition-colors hover:text-white" data-testid={`landing-nav-${n.id}`}>
                {n.label}
              </button>
            ))}
          </nav>
          <button onClick={() => scrollToId("apply")} data-testid="header-apply-btn" className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-electric px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-electric-hover">
            Apply now <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </header>

      {done ? (
        <div className="mx-auto max-w-2xl px-6 py-20">
          <div className="rounded-lg border border-line bg-white p-10 text-center" data-testid="apply-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-energy" />
            <h2 className="mt-4 font-heading text-2xl font-semibold text-navy">Application received!</h2>
            <p className="mx-auto mt-2 max-w-md text-slatey">
              Thanks — we've created your account and received your application{rep ? ` through ${rep.name}` : ""}. You can track its status any time from your dashboard, and we'll email your Letter of Authorization shortly.
            </p>
            <Button onClick={() => navigate("/portal")} className="mt-6 bg-electric text-white hover:bg-electric-hover" data-testid="apply-go-dashboard">
              Go to my dashboard
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Hero */}
          <section className="relative overflow-hidden bg-navy px-6 py-20 text-white md:px-12 md:py-28">
            <div className="grid-texture pointer-events-none absolute inset-0 opacity-40" />
            <div className="pointer-events-none absolute -right-40 -top-24 h-[440px] w-[440px] rounded-full bg-electric/[0.14] blur-[130px]" />
            <div className="pointer-events-none absolute -left-32 top-1/3 h-[380px] w-[380px] rounded-full bg-energy/[0.1] blur-[130px]" />
            <div className="relative mx-auto max-w-shell">
              <div className="max-w-3xl">
                <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.22em] text-energy">
                  <span className="h-px w-8 bg-energy" /> Commercial Energy Program
                </p>
                <h1 className="mt-5 font-heading text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                  Lower your commercial energy costs, with experts in your corner.
                </h1>
                <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/70">
                  Innervation Energy Group is a commercial energy brokerage that represents your
                  business, not the supplier. We shop the market, make providers compete, and manage
                  your contracts, all at no cost to you.
                </p>
                <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                  <button onClick={() => scrollToId("apply")} data-testid="hero-apply-btn" className="inline-flex items-center justify-center gap-2 rounded-md bg-electric px-8 py-4 text-sm font-medium text-white transition-colors hover:bg-electric-hover">
                    Start your application <ArrowRight className="h-4 w-4" />
                  </button>
                  <button onClick={() => scrollToId("how")} className="inline-flex items-center justify-center gap-2 rounded-md border border-white/25 px-8 py-4 text-sm font-medium text-white transition-colors hover:border-energy hover:text-energy">
                    See how it works
                  </button>
                </div>
                {rep && (
                  <p className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-sm text-white/70" data-testid="rep-attribution">
                    <Handshake className="h-4 w-4 text-energy" />
                    Submitted through {rep.name}{rep.company_name ? ` · ${rep.company_name}` : ""}
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* Program overview */}
          <section id="about" className="scroll-mt-20 bg-white py-20 md:py-28">
            <div className="mx-auto grid max-w-shell gap-12 px-6 md:grid-cols-2 md:items-center md:px-12">
              <div>
                <p className="overline text-electric">The Program</p>
                <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-navy sm:text-4xl">
                  A smarter way to buy commercial energy.
                </h2>
                <p className="mt-5 text-lg leading-relaxed text-slatey">
                  Most businesses overpay because they renew with whatever their supplier offers. We change
                  that. As an independent brokerage, we take your usage to a vetted network of suppliers and
                  run a competitive process, so you get a rate and contract structured around your business,
                  not the supplier's margins.
                </p>
                <p className="mt-4 text-lg leading-relaxed text-slatey">
                  You stay in control the whole way. We advise, you decide, and there's never a fee for our
                  brokerage service.
                </p>
              </div>
              <div className="space-y-4">
                {[
                  ["We represent you", "Our only job is to get your business the best outcome, independently of any supplier."],
                  ["Suppliers compete", "A competitive process puts downward pressure on your rate automatically."],
                  ["No cost, ever", "Our brokerage service is free to your business and fully transparent."],
                ].map(([t, d]) => (
                  <div key={t} className="flex items-start gap-4 rounded-lg border border-line bg-ice/60 p-5">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-energy" />
                    <div>
                      <p className="font-heading text-base font-semibold text-navy">{t}</p>
                      <p className="mt-1 text-sm leading-relaxed text-slatey">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Services */}
          <section id="services" className="scroll-mt-20 bg-navy py-20 text-white md:py-28">
            <div className="mx-auto max-w-shell px-6 md:px-12">
              <p className="overline text-energy">What We Broker</p>
              <h2 className="mt-4 max-w-2xl font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
                Energy solutions tailored to your operation.
              </h2>
              <div className="mt-12 grid gap-px overflow-hidden rounded-lg border border-white/10 bg-white/10 md:grid-cols-3">
                {SERVICES.map((s, i) => (
                  <div key={s.slug} className="flex h-full flex-col bg-navy p-8" data-testid={`landing-service-${s.slug}`}>
                    <div className="flex h-14 w-14 items-center justify-center border border-white/20 text-energy">
                      <ServiceIcon name={s.icon} className="h-7 w-7" />
                    </div>
                    <h3 className="mt-7 font-heading text-xl font-medium text-white">{s.title}</h3>
                    <p className="mt-3 flex-1 text-[15px] leading-relaxed text-white/65">{s.short}</p>
                    <span className="mt-6 font-mono text-xs uppercase tracking-[0.15em] text-white/40">0{i + 1}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* How it works */}
          <section id="how" className="scroll-mt-20 bg-ice py-20 md:py-28">
            <div className="mx-auto max-w-shell px-6 md:px-12">
              <div className="mx-auto max-w-2xl text-center">
                <p className="overline text-electric">How It Works</p>
                <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-navy sm:text-4xl">
                  From your energy bill to a better deal in four steps.
                </h2>
                <p className="mt-4 text-lg leading-relaxed text-slatey">We handle the heavy lifting and keep you informed at every stage.</p>
              </div>
              <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {STEPS.map((s, i) => (
                  <div key={i} className="relative rounded-lg border border-line bg-white p-6" data-testid={`landing-step-${i + 1}`}>
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-navy text-energy"><s.icon className="h-5 w-5" /></div>
                    <p className="mt-4 font-mono text-xs font-semibold text-electric">Step {i + 1}</p>
                    <h3 className="mt-1 font-heading text-base font-semibold text-navy">{s.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slatey">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Why IEG */}
          <section id="why" className="scroll-mt-20 bg-white py-20 md:py-28">
            <div className="mx-auto max-w-shell px-6 md:px-12">
              <div className="max-w-2xl">
                <p className="overline text-electric">Why Innervation</p>
                <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-navy sm:text-4xl">
                  Advisors on your side, from first quote to renewal.
                </h2>
              </div>
              <div className="mt-12 grid gap-px overflow-hidden rounded-lg border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
                {WHY.map((w) => (
                  <div key={w.title} className="bg-white p-7">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-energy/10 text-energy"><w.icon className="h-5 w-5" /></div>
                    <h3 className="mt-5 font-heading text-base font-semibold text-navy">{w.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slatey">{w.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="scroll-mt-20 bg-ice py-20 md:py-28">
            <div className="mx-auto max-w-3xl px-6 md:px-12">
              <p className="overline text-electric">Questions</p>
              <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-navy sm:text-4xl">Common questions, answered.</h2>
              <Accordion type="single" collapsible className="mt-8" data-testid="landing-faq">
                {FAQS.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-line">
                    <AccordionTrigger className="text-left font-heading text-base font-medium text-navy hover:no-underline">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-[15px] leading-relaxed text-slatey">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

          {/* Apply form */}
          <section id="apply" className="scroll-mt-20 bg-navy py-20 md:py-28">
            <div className="mx-auto max-w-3xl px-6 md:px-12">
              <div className="text-center">
                <p className="overline text-energy">Apply Now</p>
                <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-white sm:text-4xl">Start your application</h2>
                <p className="mx-auto mt-4 max-w-xl text-lg leading-relaxed text-white/70">
                  It takes just a few minutes. Create your account, tell us about your business, and upload a recent bill.
                </p>
              </div>

              <form onSubmit={submit} className="mt-10 space-y-6" data-testid="public-apply-form">
                {/* Your account */}
                <section className="rounded-lg border border-line bg-white p-5 md:p-6">
                  <h3 className="mb-4 font-heading text-sm font-semibold text-navy">Create your account</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div><Label>Your name</Label><Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="pa-name" /></div>
                    <div><Label>Business name</Label><Input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="mt-1.5" data-testid="pa-company" /></div>
                    <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" data-testid="pa-email" /></div>
                    <div><Label>Phone</Label><Input required value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" data-testid="pa-phone" /></div>
                    <div className="sm:col-span-2"><Label>Create a password</Label><Input type="password" required value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5" data-testid="pa-password" placeholder="min 8 characters" /></div>
                  </div>
                </section>

                {/* State + services */}
                <section className="rounded-lg border border-line bg-white p-5 md:p-6">
                  <h3 className="mb-4 font-heading text-sm font-semibold text-navy">Your energy needs</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label>State</Label>
                      <select required value={form.state} onChange={(e) => { set("state", e.target.value); setServices([]); }} data-testid="pa-state" className="mt-1.5 w-full rounded-md border border-line bg-white px-3 py-2 text-sm text-navy">
                        <option value="">Select a state…</option>
                        {states.map(([abbr, name]) => (<option key={abbr} value={abbr}>{name}</option>))}
                      </select>
                    </div>
                    <div>
                      <Label>Current agreement expiration</Label>
                      <Input type="date" value={form.agreement_expiration} onChange={(e) => set("agreement_expiration", e.target.value)} className="mt-1.5" data-testid="pa-expiration" />
                    </div>
                  </div>
                  {form.state && (
                    <div className="mt-4">
                      <Label>Services</Label>
                      {available.length === 0 ? (
                        <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="pa-no-services">
                          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                          We don't currently serve {stateInfo?.name}. Please contact us for options.
                        </div>
                      ) : (
                        <div className="mt-2 space-y-2">
                          {available.map((s) => (
                            <label key={s} className={cn("flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm", services.includes(s) ? "border-electric bg-electric/5" : "border-line bg-white")}>
                              <input type="checkbox" checked={services.includes(s)} onChange={() => toggleService(s)} data-testid={`pa-service-${s}`} className="h-4 w-4 accent-electric" />
                              <span className="font-medium text-navy">{SERVICE_LABELS[s]}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </section>

                {/* Decision maker */}
                <section className="rounded-lg border border-line bg-white p-5 md:p-6">
                  <h3 className="mb-1 font-heading text-sm font-semibold text-navy">Decision maker</h3>
                  <p className="mb-4 text-xs text-slatey">Leave blank if it's you.</p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div><Label>Name</Label><Input value={form.dm_name} onChange={(e) => set("dm_name", e.target.value)} className="mt-1.5" data-testid="pa-dm-name" /></div>
                    <div><Label>Title</Label><Input value={form.dm_title} onChange={(e) => set("dm_title", e.target.value)} className="mt-1.5" /></div>
                    <div><Label>Email</Label><Input type="email" value={form.dm_email} onChange={(e) => set("dm_email", e.target.value)} className="mt-1.5" /></div>
                    <div><Label>Phone</Label><Input value={form.dm_phone} onChange={(e) => set("dm_phone", e.target.value)} className="mt-1.5" /></div>
                  </div>
                </section>

                {/* Bills */}
                <section className="rounded-lg border border-line bg-white p-5 md:p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading text-sm font-semibold text-navy">Upload your bill</h3>
                    <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-line bg-white px-3 py-1.5 text-xs font-medium text-navy hover:border-navy/40" data-testid="pa-add-file">
                      <Plus className="h-3.5 w-3.5" /> Add file
                      <input type="file" className="hidden" onChange={addFile} accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" />
                    </label>
                  </div>
                  {files.length === 0 ? (
                    <p className="mt-2 text-xs text-slatey">Attach a recent electric and/or gas bill (PDF or image).</p>
                  ) : (
                    <ul className="mt-3 space-y-2">
                      {files.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 rounded-md border border-line bg-ice p-2">
                          <span className="min-w-0 flex-1 truncate text-sm text-navy">{f.file.name}</span>
                          <select value={f.label} onChange={(e) => setFileLabel(i, e.target.value)} className="rounded border border-line bg-white px-2 py-1 text-xs">
                            <option value="electric_bill">Electric bill</option>
                            <option value="gas_bill">Gas bill</option>
                            <option value="other_bill">Other</option>
                          </select>
                          <button type="button" onClick={() => removeFile(i)} className="text-slatey hover:text-red-600"><X className="h-4 w-4" /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-4">
                    <Label>Notes (optional)</Label>
                    <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1.5" />
                  </div>
                </section>

                <Button type="submit" disabled={busy} className="w-full bg-electric text-white hover:bg-electric-hover" data-testid="pa-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit application"}
                </Button>
                <p className="text-center text-xs text-white/60">
                  Already have an account? <Link to="/login" className="font-medium text-energy hover:underline">Sign in</Link>
                </p>
              </form>
            </div>
          </section>

          {/* Footer (self-contained) */}
          <footer className="bg-navy-dark border-t border-white/10 px-6 py-10 text-center md:px-12">
            <img src="/ieg-lockup-dark.png?v=4" alt="Innervation Energy Group" className="mx-auto h-9 w-auto" />
            <p className="mx-auto mt-4 max-w-xl text-xs leading-relaxed text-white/45">
              Innervation Energy Group is a commercial energy advisory and brokerage firm. Service availability
              varies by state and market. Our brokerage service is provided at no cost to your business.
            </p>
            <p className="mt-4 text-xs text-white/35">© {new Date().getFullYear()} Innervation Energy Group. All rights reserved.</p>
          </footer>
        </>
      )}
    </div>
  );
}
